//File: src/app/services/auth.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';

import { Observable, from, switchMap, catchError, map, BehaviorSubject, of } from 'rxjs';
import { throwError as observableThrowError } from 'rxjs';

import { DeviceService } from './device.service';
import { DeviceManagementApiService } from './device-management.api.service';
import { AlertService } from './alert.service';
import { environment } from '../../environments/environment';

export interface JwtPayload {
  sub?: string;        // subject (often username or user id)
  username?: string;   // some backends use 'username'
  preferred_username?: string;
  email?: string;
  exp?: number;        // epoch seconds
  [k: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // Prefer env URL, but fall back to same-origin /api in case env is empty
  private baseUrl = environment.apiBaseUrl || '/api';

  /** Emits the current username (or null if not logged in). */
  private usernameSubject = new BehaviorSubject<string | null>(null);
  /** Public observable for components to subscribe to. */
  public readonly username$ = this.usernameSubject.asObservable();

  constructor(
    private http: HttpClient,
    private deviceService: DeviceService,
    private router: Router,
    private deviceApi: DeviceManagementApiService,
    private alerts: AlertService
  ) {
    // Hydrate username from stored token on app start / reload
    this.hydrateFromToken();

    // Keep in sync across browser tabs/windows
    window.addEventListener('storage', (e) => {
      if (e.key === 'access_token') this.hydrateFromToken();
    });
  }

  /**
   * Logs in the user by sending credentials and device info to the backend.
   * Stores the access token in localStorage upon successful authentication.
   */
  login(username: string, password: string): Observable<any> {
    return from(this.deviceService.getDeviceInfo()).pipe(
      switchMap(deviceInfo =>
        this.http.post<any>(`${this.baseUrl}/login`, {
          username,
          password,
          device_info: deviceInfo
        }).pipe(
          switchMap(response => {
            if (response && response.access_token) {
              localStorage.setItem('access_token', response.access_token);
              localStorage.setItem('device_info', JSON.stringify(deviceInfo));
              this.setUsernameFromToken(response.access_token);
              // Ensure a device record exists in CouchDB for this device
              return this.ensureDeviceRegistered$().pipe(map(() => response));
            }
            return of(response);
          }),
          catchError(this.handleError)
        )
      )
    );
  }

  /**
   * Logs out the user by clearing tokens and navigating to login.
   */
  logout(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('device_info');
    this.usernameSubject.next(null);
    this.router.navigate(['/login']);
  }

  /**
   * Checks if the user is authenticated by verifying token validity.
   */
  isAuthenticated(): boolean {
    return this.getTokenRemainingTime() > 0;
  }

  /**
   * Retrieves the access token from localStorage.
   */
  getToken(): string | null {
    return localStorage.getItem('access_token');
  }

  /**
   * Milliseconds remaining until token expiry (0 if missing/expired).
   */
  getTokenRemainingTime(): number {
    const token = this.getToken();
    if (!token) return 0;
    const payload = this.decodeJwt(token);
    if (!payload?.exp) return 0;
    const expMs = payload.exp * 1000;
    return Math.max(0, expMs - Date.now());
  }

  /**
   * Human-friendly remaining time.
   */
getFormattedRemainingTime(): string {
  const ms = this.getTokenRemainingTime();
  if (ms <= 0) return 'Expired';

  const minutes = Math.floor(ms / 60000) % 60;
  const hours = Math.floor(ms / 3600000) % 24;
  const days = Math.floor(ms / (24 * 3600000));

  // If more than 1 day: only show days
  if (days > 1) {
    return `${days}d`;
  }

  // If 0 days and more than 1 hour: show days and hours only
  if (days === 0 && hours > 1) {
    return `0d ${hours}h`;
  }

  // Otherwise: same as existing functionality
  return `${days}d ${hours}h ${minutes}m`;
}

  /**
   * Optionally refreshes the access token.
   */
  refreshToken(): Observable<any> {
    const deviceInfo = localStorage.getItem('device_info');
    return this.http.post<any>(`${this.baseUrl}/refresh`, {
      device_info: deviceInfo
    }).pipe(
      map(response => {
        if (response && response.access_token) {
          localStorage.setItem('access_token', response.access_token);
          // Optionally update username from refreshed token (usually unchanged)
          this.setUsernameFromToken(response.access_token);
        }
        return response;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Synchronous getter if you need the current username value.
   */
  getUsername(): string | null {
    return this.usernameSubject.value;
  }

  // ------------------- Internals -------------------

  /** Decode JWT payload safely (no throws). */
  private decodeJwt(token: string): JwtPayload | null {
    try {
      const base64Url = token.split('.')[1];
      if (!base64Url) return null;

      // Base64Url -> Base64
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

      // Decode
      const jsonStr = atob(base64);

      // Handle potential UTF-8 characters
      const decoded = decodeURIComponent(
        jsonStr.split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
      );

      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

  /** Extract a username-like claim and push to subject. */
  private setUsernameFromToken(token: string): void {
    const p = this.decodeJwt(token);
    const name =
      p?.username ??
      p?.preferred_username ??
      p?.email ??
      p?.sub ??
      null;

    this.usernameSubject.next(name);
  }

  /** Restore username from stored token (or clear if expired/absent). */
  private hydrateFromToken(): void {
    const token = this.getToken();
    if (!token) {
      this.usernameSubject.next(null);
      return;
    }

    if (this.getTokenRemainingTime() <= 0) {
      this.logout();
      return;
    }

    this.setUsernameFromToken(token);
  }

  /**
   * Handles HTTP errors into user-friendly messages.
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred!';
    if (error.error instanceof ErrorEvent) {
      // Client-side/network error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Backend error
      switch (error.status) {
        case 400:
          errorMessage = 'Bad Request. Please check your input.';
          break;
        case 401:
          errorMessage = 'Unauthorized. Please check your username / password.';
          break;
        case 403:
          errorMessage = 'Forbidden. You do not have permission to perform this action.';
          break;
        case 404:
          errorMessage = 'Not Found. The requested resource could not be found.';
          break;
        case 500:
          errorMessage = 'Internal Server Error. Please try again later.';
          break;
        default:
          errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
          break;
      }

      if (error.error && error.error.message) {
        if (String(error.error.message).toLowerCase().includes('password')) {
          errorMessage = 'Invalid password. Please try again.';
        }
      }
    }
    return observableThrowError(() => new Error(errorMessage));
  }

  /** Legacy example kept for reference; safe to delete. */
  private OLD__handleError(error: HttpErrorResponse) {
    let errorMessage = 'An unknown error occurred!';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return observableThrowError(() => new Error(errorMessage));
  }

  /**
   * Ensure the current device has a record in the backend. If missing, create it
   * and notify the user. Persist the backend device _id in localStorage for reuse.
   */
  private ensureDeviceRegistered$(): Observable<void> {
    // Always ensure we have a stable, local device id first (bare uuid)
    const localId = this.deviceService.getOrCreateDeviceId();
    console.log('auth.service: checking for local device:', localId);
    return this.deviceApi.get(localId).pipe(
      // If found, persist id from server (supports _id or id) and exit
      map((resp: any) => {
        const data = resp?.data || {};
        const serverId: string = data._id || data.id || localId;
        const storeVal = serverId.startsWith('device:') ? serverId : `device:${serverId}`;
        localStorage.setItem('device:id', storeVal);
        console.log('auth.service: found registered device id:', storeVal);
        return void 0;
      }),
      catchError((err) => {
        // If not found (404), create the device record using the existing endpoint
        console.log('auth.service: device not found on server, creating...', err);
        if (err?.status === 404) {
          return this.deviceApi.create().pipe(
            map((createResp: any) => {
              const data = createResp?.data || {};
              const newId: string = data._id || data.id || `device:${localId}`;
              const storeVal = newId.startsWith('device:') ? newId : `device:${newId}`;
              localStorage.setItem('device:id', storeVal);
              this.alerts.info('Registered this device for your account.', { autoClose: true, life: 4000 });
              return void 0;
            }),
            catchError((_createErr) => {
              // Don't block login if device registration fails; just continue silently
              console.log('auth.service: device registration failed:', _createErr);
              return of(void 0);
            })
          );
        }
        // For other errors, do not block login
        return of(void 0);
      })
    );
  }
}
